<script setup lang="ts">
import type {
  TypeDownloadableDocumentSkeleton,
  TypeDownloadableDocumentWithoutUnresolvableLinksResponse,
} from '~/types/contentful'
import type { CustomElementsDependencies } from '~/types/custom-elements'
import MarketingAsset from '~/components/MarketingAsset.vue'

declare global {
  interface Window {
    __WPN_CUSTOM_ELEMENTS__?: CustomElementsDependencies
  }
}

const props = defineProps<{
  entry: string
  debug?: boolean | string
}>()

const isLoading = ref(false)
const data = ref<TypeDownloadableDocumentWithoutUnresolvableLinksResponse>()
const isVisible = ref(false)

const isDebug = computed(() => {
  return (
    props.debug === '' ||
    props.debug === true ||
    props.debug === 'true' ||
    props.debug === '1'
  )
})

const shouldRender = computed(() => {
  return !!data.value && isVisible.value
})

async function loadDocument() {
  data.value = undefined
  isVisible.value = false

  if (!props.entry) {
    if (isDebug.value) {
      console.log('[DownloadDocument] missing required "entry" prop')
    }
    return
  }

  if (!window.__WPN_CUSTOM_ELEMENTS__) {
    if (isDebug.value) {
      console.log(
        '[DownloadDocument] custom element dependencies are unavailable',
      )
    }
    return
  }

  isLoading.value = true

  try {
    const deps = window.__WPN_CUSTOM_ELEMENTS__
    const locale = deps.getLocale()

    if (isDebug.value) {
      console.log('[DownloadDocument] fetching entry', {
        entryId: props.entry,
        locale,
      })
    }

    const response =
      await deps.client.withoutUnresolvableLinks.getEntries<TypeDownloadableDocumentSkeleton>(
        {
          content_type: 'downloadableDocument',
          include: 2,
          limit: 1,
          'sys.id': props.entry,
          locale,
        },
      )

    const entry = response.items[0]

    if (!entry) {
      if (isDebug.value) {
        console.log('[DownloadDocument] no downloadable document found', {
          entryId: props.entry,
        })
      }
      return
    }

    data.value = entry

    const visibilityDecision = deps.visibilityDecision(
      entry.fields?.visibility,
      { debug: isDebug.value },
    )
    isVisible.value = visibilityDecision.visible === true

    if (isDebug.value) {
      console.log('[DownloadDocument] gate checks', {
        id: entry.sys.id,
        visibilityDecision,
        isVisible: isVisible.value,
        shouldRender: shouldRender.value,
      })
    }
  } finally {
    isLoading.value = false
  }
}

watch(
  () => props.entry,
  () => {
    loadDocument()
  },
  { immediate: true },
)
</script>

<template>
  <MarketingAsset v-if="shouldRender" :data="data" />
  <div v-else-if="isLoading" class="loading" aria-live="polite" />
</template>

<style lang="scss" scoped>
.loading {
  display: none;
}
</style>
